import { CheckIcon, IconButton, type PlaybackTransport } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import { formatNumber } from "@/utils";
import {
  freezeCandidateChipText,
  freezeCandidateIndexAt,
  QUALITY_CANDIDATE_DOT_COLOR,
  type QualityCandidateSpan,
} from "./qualityCandidates";
import { freezeCandidateDetailText } from "./segmentTimeline";
import { usePlaybackSelection } from "./usePlaybackSelection";
// 출처 칩과 같은 칩 모양(색 점·줄바꿈)을 쓴다.
import styles from "./SegmentChipRow.module.css";

// 칩 줄이 영상 아래를 덮지 않게 하는 상한. 출처 칩 줄과 같은 값이다. 넘는 후보는 재생 바 행이
// 모두 보여 준다.
const MAX_CHIPS = 48;

/**
 * 재생 바 바로 위의 영상 정지 후보 칩. 후보 구간 하나에 칩 하나를 시간순으로 놓는다.
 *
 * - 칩을 누르면 영상을 그 구간 처음으로 옮긴다.
 * - 재생 위치가 든 후보의 칩을 강조한다. 따로 선택하는 상태는 없다.
 * - 후보는 제안일 뿐이다. 칩은 결함 태그를 붙이지 않는다. 리뷰어가 구간을 보고 태그를 고른다.
 * - 검토 완료가 확인한 후보는 칩 끝에 체크 표시를 둔다. 누가 언제 확인했는지는 툴팁이 말한다.
 * - c6 는 타임라인 띠를 `aria-hidden` 으로 둔다. 그래서 스크린 리더는 이 줄로만 후보에 접근한다.
 */
export function FreezeCandidateChips({
  spans,
  transport,
  onSeekToSeconds,
}: {
  spans: readonly QualityCandidateSpan[];
  transport: PlaybackTransport | null;
  onSeekToSeconds?: (seconds: number) => void;
}): ReactNode {
  // 후보 index 만 고른다. 그래서 재생 중 매 프레임이 아니라 후보가 바뀔 때만 다시 그린다.
  const current = usePlaybackSelection(transport, (snapshot) =>
    freezeCandidateIndexAt(spans, snapshot.currentTimeSeconds),
  );
  const shown = spans.slice(0, MAX_CHIPS);
  const hidden = spans.length - shown.length;

  return (
    <div className={styles.row} role="group" aria-label="영상 정지 후보 칩">
      {shown.map((span, index) => {
        const caption = freezeCandidateDetailText(span);
        const isCurrent = index === current;
        return (
          <HatTextTooltip key={span.id} text={caption} placement="bottom" align="start">
            <IconButton
              type="button"
              size="sm"
              variant={isCurrent ? "solid" : "surface"}
              tone="neutral"
              aria-current={isCurrent ? "true" : undefined}
              // 칩에는 시각을 표시할 수 없다. 그래서 이 라벨이 시각과 길이, 확인 기록을 전달한다.
              aria-label={`${caption}. 누르면 이 구간 처음으로 영상을 옮깁니다.`}
              disabled={onSeekToSeconds === undefined}
              onClick={() => onSeekToSeconds?.(span.startSeconds)}
            >
              <span className={styles.mark} aria-hidden="true">
                <span className={styles.dot} style={{ background: QUALITY_CANDIDATE_DOT_COLOR }} />
                {freezeCandidateChipText(span)}
                {span.check === null ? null : <CheckIcon />}
              </span>
            </IconButton>
          </HatTextTooltip>
        );
      })}
      {hidden > 0 ? (
        <span className={styles.more}>
          외 {formatNumber(hidden)}개가 더 있습니다. 재생 바의 영상 정지 후보 행에서 모두 볼 수
          있습니다.
        </span>
      ) : null}
    </div>
  );
}
